import { useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add as AddIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { exercisesApi } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'
import { CommaListField, SectionLabel } from './primitives'
import { HotspotCanvas } from './HotspotCanvas'
import type { ChoiceContent, PictureContent, TrueFalseContent } from '../contentTypes'

/**
 * Редакторы типов с выбором: варианты ответа, верно/неверно и картинки.
 */

type EditorProps<T> = {
  content: T
  onChange: (content: T) => void
  disabled?: boolean
}

export function ChoiceEditor({ content, onChange, disabled }: EditorProps<ChoiceContent>) {
  const { t } = useTranslation()
  const multiple = content.multiple === true

  const setOption = (index: number, patch: Partial<{ text: string; correct: boolean }>) => {
    const options = content.options.map((option, i) => (i === index ? { ...option, ...patch } : option))

    // Одиночный выбор с двумя верными нерешаем: ученик отметит только один
    if (!multiple && patch.correct) {
      onChange({ ...content, options: options.map((option, i) => ({ ...option, correct: i === index })) })

      return
    }

    onChange({ ...content, options })
  }

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        multiline
        size="small"
        label={t('exercises.editor.question')}
        value={content.question}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, question: event.target.value })}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={multiple}
            disabled={disabled}
            onChange={(event) => {
              const next = event.target.checked
              // При переходе к одиночному оставляем один верный вариант
              const options = next
                ? content.options
                : content.options.map((option, index) => ({
                    ...option,
                    correct: index === content.options.findIndex((item) => item.correct),
                  }))
              onChange({ ...content, multiple: next, options })
            }}
          />
        }
        label={t('exercises.editor.multipleAnswers')}
      />

      <Box>
        <SectionLabel text={t('exercises.editor.options')} hint={t('exercises.editor.optionsHint')} />
        <Stack spacing={1}>
          {content.options.map((option, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              {multiple ? (
                <Checkbox
                  checked={option.correct === true}
                  disabled={disabled}
                  onChange={(event) => setOption(index, { correct: event.target.checked })}
                />
              ) : (
                <Radio
                  checked={option.correct === true}
                  disabled={disabled}
                  onChange={() => setOption(index, { correct: true })}
                />
              )}
              <TextField
                fullWidth
                size="small"
                value={option.text}
                disabled={disabled}
                onChange={(event) => setOption(index, { text: event.target.value })}
              />
              <IconButton
                size="small"
                disabled={disabled || content.options.length <= 2}
                onClick={() => onChange({ ...content, options: content.options.filter((_, i) => i !== index) })}
                aria-label={t('common.delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() => onChange({ ...content, options: [...content.options, { text: '' }] })}
            >
              {t('exercises.editor.addOption')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
}

export function TrueFalseEditor({ content, onChange, disabled }: EditorProps<TrueFalseContent>) {
  const { t } = useTranslation()
  const allowNotStated = content.allow_not_stated === true

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        size="small"
        label={t('exercises.editor.passage')}
        helperText={t('exercises.editor.passageHint')}
        value={content.text ?? ''}
        disabled={disabled}
        onChange={(event) => onChange({ ...content, text: event.target.value })}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={allowNotStated}
            disabled={disabled}
            onChange={(event) => onChange({ ...content, allow_not_stated: event.target.checked })}
          />
        }
        label={t('exercises.editor.allowNotStated')}
      />

      <Box>
        <SectionLabel text={t('exercises.editor.statements')} />
        <Stack spacing={1}>
          {content.statements.map((statement, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                fullWidth
                size="small"
                value={statement.text}
                disabled={disabled}
                onChange={(event) => {
                  const statements = [...content.statements]
                  statements[index] = { ...statement, text: event.target.value }
                  onChange({ ...content, statements })
                }}
              />
              <TextField
                select
                size="small"
                sx={{ minWidth: 150 }}
                value={statement.answer ?? 'true'}
                disabled={disabled}
                onChange={(event) => {
                  const statements = [...content.statements]
                  statements[index] = { ...statement, answer: event.target.value as TrueFalseContent['statements'][number]['answer'] }
                  onChange({ ...content, statements })
                }}
              >
                <MenuItem value="true">{t('exercises.editor.answerTrue')}</MenuItem>
                <MenuItem value="false">{t('exercises.editor.answerFalse')}</MenuItem>
                {allowNotStated && <MenuItem value="not_stated">{t('exercises.editor.answerNotStated')}</MenuItem>}
              </TextField>
              <IconButton
                size="small"
                disabled={disabled || content.statements.length <= 1}
                onClick={() => onChange({ ...content, statements: content.statements.filter((_, i) => i !== index) })}
                aria-label={t('common.delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() =>
                onChange({ ...content, statements: [...content.statements, { text: '', answer: 'true' }] })
              }
            >
              {t('exercises.editor.addStatement')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Stack>
  )
}

/** Загрузка картинки: место расходуется из хранилища учителя */
function ImageUploadButton({
  onUploaded,
  disabled,
  label,
}: {
  onUploaded: (path: string, url: string) => void
  disabled?: boolean
  label: string
}) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const upload = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const result = await exercisesApi.uploadImage(file)
      onUploaded(result.path, result.url)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) void upload(file)
          event.target.value = ''
        }}
      />
      <Button
        size="small"
        startIcon={<ImageIcon />}
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? t('common.loading') : label}
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

export function PictureEditor({ content, onChange, disabled }: EditorProps<PictureContent>) {
  const { t } = useTranslation()
  const variant = content.variant

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label={t('exercises.editor.pictureVariant')}
        value={variant}
        disabled={disabled}
        onChange={(event) =>
          onChange({
            variant: event.target.value as PictureContent['variant'],
            image_path: null,
            hotspots: [],
            prompt: '',
            options: [],
          })
        }
      >
        <MenuItem value="label_hotspots">{t('exercises.editor.pictureLabelHotspots')}</MenuItem>
        <MenuItem value="pick_image">{t('exercises.editor.picturePickImage')}</MenuItem>
      </TextField>

      {variant === 'label_hotspots' ? (
        <>
          <ImageUploadButton
            label={t('exercises.editor.uploadImage')}
            disabled={disabled}
            onUploaded={(path, url) => onChange({ ...content, image_path: path, image_url: url })}
          />
          {content.image_url && (
            <HotspotCanvas
              imageUrl={content.image_url}
              hotspots={content.hotspots ?? []}
              disabled={disabled}
              onChange={(hotspots) => onChange({ ...content, hotspots })}
            />
          )}

          <Box>
            <SectionLabel text={t('exercises.editor.hotspots')} hint={t('exercises.editor.hotspotsHint')} />
            <Stack spacing={1}>
              {(content.hotspots ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {content.image_url
                    ? t('exercises.editor.hotspotsEmpty')
                    : t('exercises.editor.hotspotsNeedImage')}
                </Typography>
              )}
              {(content.hotspots ?? []).map((hotspot, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  {/* Номер вместо полей координат: область задаётся рамкой на
                      картинке, а порядок в списке — это её подпись у ученика */}
                  <Typography sx={{ width: 24, textAlign: 'right' }} color="text.secondary">
                    {index + 1}.
                  </Typography>
                  <CommaListField
                    label={t('exercises.editor.hotspotAnswers')}
                    values={hotspot.answers ?? []}
                    disabled={disabled}
                    onChange={(answers) => {
                      const hotspots = [...(content.hotspots ?? [])]
                      hotspots[index] = { ...hotspot, answers }
                      onChange({ ...content, hotspots })
                    }}
                  />
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() =>
                      onChange({ ...content, hotspots: (content.hotspots ?? []).filter((_, i) => i !== index) })
                    }
                    aria-label={t('common.delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>
        </>
      ) : (
        <>
          <TextField
            fullWidth
            size="small"
            label={t('exercises.editor.prompt')}
            value={content.prompt ?? ''}
            disabled={disabled}
            onChange={(event) => onChange({ ...content, prompt: event.target.value })}
          />

          <Box>
            <SectionLabel text={t('exercises.editor.pictureOptions')} hint={t('exercises.editor.pickImageHint')} />
            <Stack spacing={1}>
              {(content.options ?? []).map((option, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Radio
                    checked={option.correct === true}
                    disabled={disabled}
                    onChange={() =>
                      onChange({
                        ...content,
                        options: (content.options ?? []).map((item, i) => ({ ...item, correct: i === index })),
                      })
                    }
                  />
                  {option.image_url && (
                    <Box component="img" src={option.image_url} alt="" sx={{ width: 64, height: 64, objectFit: 'cover' }} />
                  )}
                  <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                    {option.image_path}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() =>
                      onChange({ ...content, options: (content.options ?? []).filter((_, i) => i !== index) })
                    }
                    aria-label={t('common.delete')}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <ImageUploadButton
                label={t('exercises.editor.addPicture')}
                disabled={disabled}
                onUploaded={(path, url) =>
                  onChange({
                    ...content,
                    options: [...(content.options ?? []), { image_path: path, image_url: url }],
                  })
                }
              />
            </Stack>
          </Box>
        </>
      )}
    </Stack>
  )
}
